import { v4 as uuidv4 } from 'uuid';
import type { FabricObject } from 'fabric';
import { TimelineClipEntry } from './attachment-graph';

export const GROUP_VIDEO_LANE = 'group:video';
export const GROUP_TEXT_LANE = 'group:text';

/**
 * Fresh lane key for any genuinely new element (video, pill, sobreimpreso, auto-fit text, plain
 * text) that must not merge into the shared `group:video`/`group:text` row: sharing that lane
 * would expose it to the time-overlap attachment heuristic (`attachment-graph.ts`), which could
 * "adopt" it into an unrelated video/subtitle just because it happens to overlap in time. Stamped
 * at creation time so it survives save/reload as its own track, rendered at its natural
 * (front-most) `trackOrder` position — same as a new image or shape.
 */
export const createOwnLaneKey = (): string => `own-lane:${uuidv4()}`;

/**
 * Runtime lane registry, owned by TimelinePanel and shared with TrackArea/LayerColumn. A "lane" is
 * the drag/collision/duplicate unit — a track-row can hold more than one clip (e.g. the shared
 * video/text group), and every clip sharing a lane key renders in the same row and never collides
 * across rows.
 *
 * The lane key itself is read from `Clip.laneKey` (mirrors the persisted `Layer.laneKey`, see
 * model/types.ts) whenever present — that's what makes a track survive save/reload. Absent means
 * "belongs to the shared video/text group row"; this registry is just an in-session cache over
 * that.
 */
export type LaneRegistry = {
  laneKeyByClipId: Map<string, string>;
};

/** Lane key for an entry: cached if already resolved this session, otherwise derived from the persisted `Clip.laneKey` (or the implicit shared-group default) and remembered. */
export const resolveLaneKey = (entry: TimelineClipEntry, registry: LaneRegistry): string => {
  if (!entry.clip) return `solo:${entry.track.id}`;
  const cached = registry.laneKeyByClipId.get(entry.clip.id);
  if (cached) return cached;

  const laneKey =
    entry.clip.laneKey ??
    (entry.track.kind === 'video'
      ? GROUP_VIDEO_LANE
      : entry.track.kind === 'text'
        ? GROUP_TEXT_LANE
        : `solo:${entry.clip.id}`);

  registry.laneKeyByClipId.set(entry.clip.id, laneKey);
  return laneKey;
};

export const seedLaneRegistry = (entries: TimelineClipEntry[]): LaneRegistry => {
  const registry: LaneRegistry = { laneKeyByClipId: new Map() };
  entries.forEach(entry => resolveLaneKey(entry, registry));
  return registry;
};

/**
 * Entries eligible for the attachment graph (video<->subtitle association): only the shared
 * video/text group lanes. A solo lane — new track or duplicate of one — must stay isolated, never
 * auto-linked to an existing video/subtitle just because it happens to overlap in time.
 */
export const filterGroupableEntries = (entries: TimelineClipEntry[], registry: LaneRegistry): TimelineClipEntry[] =>
  entries.filter(entry => {
    if (!entry.clip) return true;
    const laneKey = resolveLaneKey(entry, registry);
    return laneKey === GROUP_VIDEO_LANE || laneKey === GROUP_TEXT_LANE;
  });

/**
 * Shared row-partitioning logic between TrackArea (clip-blocks) and LayerColumn (layer list): one
 * bucket per lane, in the order each lane first appears in `entries` (which follows the model's
 * `trackOrder` — front-most layer first). Kept in one place so both columns always land on the
 * same row count, order and grouping (they're rendered side by side with synced scrolling).
 */
export type TimelineRowBucket =
  | { kind: 'video-group'; entries: TimelineClipEntry[] }
  | { kind: 'text-group'; entries: TimelineClipEntry[] }
  | { kind: 'solo'; entries: TimelineClipEntry[] };

export const partitionTimelineRows = (entries: TimelineClipEntry[], registry: LaneRegistry): TimelineRowBucket[] => {
  const rows: TimelineRowBucket[] = [];
  const bucketByLaneKey = new Map<string, TimelineRowBucket>();

  for (const entry of entries) {
    if (!entry.clip) {
      rows.push({ kind: 'solo', entries: [entry] });
      continue;
    }

    const laneKey = resolveLaneKey(entry, registry);
    let bucket = bucketByLaneKey.get(laneKey);
    if (!bucket) {
      bucket =
        laneKey === GROUP_VIDEO_LANE
          ? { kind: 'video-group', entries: [] }
          : laneKey === GROUP_TEXT_LANE
            ? { kind: 'text-group', entries: [] }
            : { kind: 'solo', entries: [] };
      bucketByLaneKey.set(laneKey, bucket);
      rows.push(bucket);
    }
    bucket.entries.push(entry);
  }

  return rows;
};

/**
 * A row in the Capas panel can represent more than one physical Fabric object (the shared
 * video/text group, or a solo lane with duplicates) — but z-order reorder (`use-layer-controls.ts`)
 * only knows how to swap with the single immediate neighbor in the raw stack, one object at a
 * time. Reordering a clip past a group that way requires one click per group member, and every
 * click in between leaves the moved clip visually interleaved with the group.
 *
 * This computes the actual target of a *row-aware* move: given `obj`'s immediate neighbor in
 * `stack` (the layer objects, back-to-front, exactly as `getLayerObjects()` builds it), if that
 * neighbor belongs to a lane with more than one member, walks the rest of the stack in the
 * direction of travel and returns the farthest member of that lane it finds — so the caller can
 * jump the whole group in a single `moveNextTo` instead of landing between two of its members.
 * Returns `null` when there's nothing to jump (no neighbor, or the neighbor's lane has a single
 * member — including when `obj` itself is a member of that same lane, e.g. reordering one clip
 * among its own group siblings), so the caller can fall back to its normal single-step move.
 *
 * The walk does NOT stop at the first object outside the lane: a shared lane's members aren't
 * necessarily one contiguous run. `build-scenes-model.ts` interleaves each scene's own video
 * between that scene's subtitles and the next scene's — with several scenes, `GROUP_TEXT_LANE`
 * (and `GROUP_VIDEO_LANE`) end up split into several separated clusters in the raw stack, even
 * though they're a single row in the model/timeline. Stopping at the first mismatch would only
 * jump past the nearest cluster; skipping mismatches and continuing to the end of the stack finds
 * the true farthest member across every cluster.
 */
export const findGroupJumpTarget = (
  stack: FabricObject[],
  entryByFabricObject: Map<FabricObject, TimelineClipEntry>,
  obj: FabricObject,
  direction: 1 | -1,
  registry: LaneRegistry
): FabricObject | null => {
  const i = stack.indexOf(obj);
  if (i < 0) return null;

  const immediateNeighbor = stack[i + direction];
  if (!immediateNeighbor) return null;

  const ownEntry = entryByFabricObject.get(obj);
  const neighborEntry = entryByFabricObject.get(immediateNeighbor);
  if (!ownEntry || !neighborEntry) return null;

  const ownLaneKey = resolveLaneKey(ownEntry, registry);
  const neighborLaneKey = resolveLaneKey(neighborEntry, registry);
  if (ownLaneKey === neighborLaneKey) return null; // same group as obj: a single step is correct

  let farEdge = immediateNeighbor;
  for (let j = i + direction; stack[j]; j += direction) {
    const candidateEntry = entryByFabricObject.get(stack[j]);
    if (candidateEntry && resolveLaneKey(candidateEntry, registry) === neighborLaneKey) farEdge = stack[j];
  }

  return farEdge === immediateNeighbor ? null : farEdge;
};
